import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, CheckCircle2, XCircle } from "lucide-react";
import { useEffect, useState } from "react";
import * as z from "zod";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Spinner } from "~/components/ui/spinner";
import {
  $testProviderConnection,
  $updateOnboardingStep,
} from "~/lib/onboarding/mutations";

const searchSchema = z.object({
  connectionId: z.string(),
});

type TestResult = {
  success: boolean;
  model?: string;
  response?: string;
  error?: string;
};

function TestConnectionScreen() {
  const navigate = useNavigate();
  const { connectionId } = Route.useSearch();
  const [isLoading, setIsLoading] = useState(true);
  const [testResult, setTestResult] = useState<TestResult | null>(null);

  useEffect(() => {
    const runTest = async () => {
      setIsLoading(true);

      try {
        const result = await $testProviderConnection({ data: { connectionId } });

        setTestResult({
          success: result.success,
          model: result.model,
          response: result.response,
        });

        if (result.success) {
          await $updateOnboardingStep({ data: { step: 2 } });

          const timeoutId = window.setTimeout(() => {
            navigate({ to: "/onboarding/provider" });
          }, 1500);

          return () => window.clearTimeout(timeoutId);
        }
      } catch (err) {
        setTestResult({
          success: false,
          error: err instanceof Error ? err.message : "Connection test failed",
        });
      } finally {
        setIsLoading(false);
      }
    };

    runTest();
  }, [connectionId, navigate]);

  return (
    <div className="flex flex-col items-center">
      <div className="mb-8 text-center">
        <h1 className="mb-2 text-3xl font-bold tracking-tight">
          Testing your connection
        </h1>
        <p className="text-muted-foreground">Verifying your API key works correctly</p>
      </div>

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          {isLoading ? (
            <>
              <div className="mx-auto mb-4">
                <Spinner className="size-12" />
              </div>
              <CardTitle>Testing Connection...</CardTitle>
              <CardDescription>
                Sending a test request to verify your API key
              </CardDescription>
            </>
          ) : testResult?.success ? (
            <>
              <div className="mx-auto mb-4">
                <CheckCircle2 className="size-12 text-green-500" />
              </div>
              <CardTitle className="text-green-600">Connection Successful!</CardTitle>
              <CardDescription>
                Model: <span className="font-medium">{testResult.model}</span>
              </CardDescription>
            </>
          ) : (
            <>
              <div className="mx-auto mb-4">
                <XCircle className="text-destructive size-12" />
              </div>
              <CardTitle className="text-destructive">Connection Failed</CardTitle>
              <CardDescription>{testResult?.error}</CardDescription>
            </>
          )}
        </CardHeader>

        {testResult && (
          <CardContent className="text-center">
            {testResult.success ? (
              <p className="text-muted-foreground text-sm">
                Returning to add more providers...
              </p>
            ) : (
              <Button
                variant="outline"
                onClick={() => navigate({ to: "/onboarding/provider" })}
              >
                <ArrowLeft className="mr-2 size-4" />
                Go Back
              </Button>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
}

export const Route = createFileRoute("/(authenticated)/onboarding/test-connection")({
  component: TestConnectionScreen,
  validateSearch: searchSchema,
});
